import {useCallback, useEffect, useRef} from 'react';
import {WebcamRefTypes} from '@chekinapp/ui';
import {importWithRetry} from '@chekin/core';

interface UseQRScannerProps {
  expectedUrl?: string;
  onSuccess?: (qrCode: string) => void;
  onError?: (error: string) => void;
}

function useQRScanner({expectedUrl, onSuccess, onError}: UseQRScannerProps) {
  const webcamRef = useRef<WebcamRefTypes>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingRef = useRef(false);

  const validateQRCode = useCallback(
    (scannedUrl: string): {isValid: boolean; error?: string} => {
      if (!expectedUrl) {
        return {
          isValid: false,
          error: 'Housing identity verification link not found',
        };
      }

      if (!scannedUrl) {
        return {
          isValid: false,
          error: 'Scanned URL is empty',
        };
      }

      const housingLinkNormalized = expectedUrl.trim().toLowerCase();
      const scannedUrlNormalized = scannedUrl.trim().toLowerCase();

      const isValid = scannedUrlNormalized === housingLinkNormalized;

      if (!isValid) {
        return {
          isValid: false,
          error: 'QR code does not match housing verification link',
        };
      }

      return {isValid: true};
    },
    [expectedUrl],
  );

  const stopScanning = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
  }, []);

  const decodeQR = useCallback(
    (dataUrl: string) => {
      try {
        if (!dataUrl.includes(';base64,')) return;

        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const jsQR = await importWithRetry(() => import('jsqr')).then(
            res => res.default,
          );
          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code?.data) {
            const validation = validateQRCode(code.data);
            if (validation.isValid) {
              stopScanning();
              onSuccess?.(code.data);
            } else {
              onError?.(validation.error || 'Invalid QR code');
            }
          }
        };

        img.src = dataUrl;
      } catch (_) {
        return;
      }
    },
    [validateQRCode, stopScanning, onSuccess, onError],
  );

  const startScanning = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }

    scanIntervalRef.current = setInterval(async () => {
      if (isProcessingRef.current) return;

      try {
        if (!webcamRef.current) return;

        isProcessingRef.current = true;
        const screenshot = await webcamRef.current.takePhoto();
        if (!screenshot) {
          isProcessingRef.current = false;
          return;
        }

        decodeQR(screenshot);
        isProcessingRef.current = false;
      } catch (_) {
        isProcessingRef.current = false;
      }
    }, 1500);
  }, [decodeQR]);

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, [stopScanning]);

  return {
    webcamRef,
    startScanning,
    stopScanning,
  };
}

export {useQRScanner};
